import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { motion } from 'motion/react';

// Motion variants for path drawing animation - matches Next.js exactly
const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i) => {
    const delay = i * 0.25;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { delay, type: "spring", duration: 1.2, bounce: 0 },
        opacity: { delay, duration: 0.01 },
      },
    };
  },
};

const PotredPathDrawing = forwardRef(({ 
  className = "w-16 h-16 text-orange-500 dark:text-orange-400",
  enablePeriodicAnimation = false,
  animateOnMount = true
}, ref) => {
  const [animationKey, setAnimationKey] = useState(0);
  const intervalRef = useRef(undefined);

  // Handle periodic animation every 6 seconds
  useEffect(() => {
    if (!enablePeriodicAnimation) return;

    const startPeriodicAnimation = () => {
      intervalRef.current = setInterval(() => {
        setAnimationKey(prev => prev + 1); // Trigger re-render with animation
      }, 6000);
    };

    // Start the interval
    startPeriodicAnimation();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enablePeriodicAnimation]);

  // Expose animation controls to parent via ref
  useImperativeHandle(ref, () => ({
    triggerAnimation: () => {
      setAnimationKey(prev => prev + 1);
    }
  }), []);

  return (
    <motion.svg
      key={enablePeriodicAnimation ? animationKey : "static"} // Re-mount for animation
      className={className}
      viewBox="0 0 24 24"
      initial="hidden"
      animate="visible"
      stroke="currentColor"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* pot knob */}
      <motion.path 
        d="m8.86 6.78-.45-1.81a2 2 0 0 1 1.45-2.43l1.94-.48a2 2 0 0 1 2.43 1.46l.45 1.8" 
        variants={draw} 
        custom={0} 
      />
      {/* pot lid */}
      <motion.path 
        d="m4 8 16-4" 
        variants={draw} 
        custom={1} 
      />
      {/* pot body (including sides/base) */}
      <motion.path 
        d="M2 12h20" 
        variants={draw} 
        custom={2} 
      />
      <motion.path 
        d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" 
        variants={draw} 
        custom={2} 
      />
    </motion.svg>
  );
});

PotredPathDrawing.displayName = 'PotredPathDrawing';

export default PotredPathDrawing;